import type { Fragment } from "../types.js";
import type { ReelProps } from "../../compositions/reelShape.js";
import { colors } from "../../brand/theme.js";
import { escapeHtml } from "../composition.js";

type Overlay = NonNullable<ReelProps["overlays"]>[number];

function resolveColor(token: string): string {
  switch (token) {
    case "primary":
      return colors.primary;
    case "secondary":
      return colors.secondary;
    case "accent":
      return colors.accent;
    case "white":
      return "#ffffff";
    case "black":
      return "#000000";
    case "fg":
      return colors.fg;
    default:
      return token;
  }
}

/**
 * Image-Overlays speichern die `src` so, wie sie in der UI funktioniert –
 * meist als absoluter Pfad (`/brand/logo.png`, `/static/uploads/<f>`,
 * `/uploads/<f>`). Im Renderer wird das HTML aber per `file://` aus dem
 * stage-Workdir geladen, dort gibt es keinen Web-Server. Wir bringen die URLs
 * deshalb in die Form, die `stageProjectDir()` bereitstellt:
 *
 *   /brand/<f>           → assets/brand/<f>
 *   /static/uploads/<f>  → assets/uploads/<f>
 *   /static/<sub>/<f>    → assets/<sub>/<f>
 *   /uploads/<f>         → assets/uploads/<f>
 *   /sounds/<f>          → assets/sounds/<f>
 *   absolute http(s)/data/blob → unverändert
 */
function normaliseAssetSrc(raw: string | undefined | null): string {
  if (!raw) return "";
  const url = String(raw).trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || url.startsWith("data:") || url.startsWith("blob:")) return url;
  const staticUploads = url.match(/^\/static\/uploads\/(.+)$/);
  if (staticUploads) return `assets/uploads/${staticUploads[1]}`;
  const staticAny = url.match(/^\/static\/(.+)$/);
  if (staticAny) return `assets/${staticAny[1]}`;
  const uploads = url.match(/^\/uploads\/(.+)$/);
  if (uploads) return `assets/uploads/${uploads[1]}`;
  const brand = url.match(/^\/brand\/(.+)$/);
  if (brand) return `assets/brand/${brand[1]}`;
  const sounds = url.match(/^\/sounds\/(.+)$/);
  if (sounds) return `assets/sounds/${sounds[1]}`;
  if (url.startsWith("/")) return url.slice(1);
  return url;
}

/**
 * Map a Reel overlay layer to a positioned HTML element wrapped in a
 * `class="clip"` for time-window visibility. Mirrors the React rendering in
 * `OverlayRenderer` inside `Reel.tsx`.
 */
export function buildOverlayHtml(overlay: Overlay, idx: number): string {
  if (overlay.hidden) return "";
  if (overlay.kind === "group") return "";

  const wrapperId = `ov-${idx}`;
  // Each overlay lives on its own track (10..19 reserved for overlays) so
  // overlapping windows on the same authored track don't trigger
  // `overlapping_clips_same_track` lint errors.
  const overlayTrack = 10 + idx;
  const transform = `translate(-50%, -50%) rotate(${overlay.rotate}deg) scale(${overlay.scale})`;
  const wrapperStyle = `position:absolute;left:${overlay.xPct}%;top:${overlay.yPct}%;transform:${transform};transform-origin:50% 50%;opacity:${overlay.opacity};pointer-events:none;`;

  let innerHtml = "";
  let dimStyle = "";

  if (overlay.kind === "text") {
    const color = resolveColor(overlay.color);
    dimStyle = overlay.maxWidthPct ? `width:${overlay.maxWidthPct}%;` : "";
    innerHtml = `<div style="font-family:Inter,system-ui,sans-serif;font-weight:${overlay.weight};font-size:${overlay.fontSize}px;color:${color};letter-spacing:${overlay.letterSpacing ? `${overlay.letterSpacing}em` : "normal"};text-align:${overlay.align};line-height:1.05;white-space:pre-wrap;word-break:break-word;${overlay.shadow ? "text-shadow:0 4px 24px rgba(0,0,0,0.55), 0 1px 2px rgba(0,0,0,0.4);" : ""}">${escapeHtml(overlay.text || "\u00A0")}</div>`;
  } else if (overlay.kind === "shape") {
    const fill = overlay.fill ? resolveColor(overlay.fill) : colors.primary;
    const stroke = overlay.stroke ? resolveColor(overlay.stroke) : "transparent";
    const sw = overlay.strokeWidth ?? 0;
    dimStyle = `width:${overlay.widthPct}%;height:${overlay.heightPct}%;`;
    let radius = "0";
    if (overlay.shape === "pill") radius = "9999px";
    else if (overlay.shape === "circle") radius = "50%";
    else if (overlay.shape === "rect") radius = `${overlay.radius ?? 0}px`;
    if (overlay.shape === "rect" || overlay.shape === "pill" || overlay.shape === "circle") {
      innerHtml = `<div style="width:100%;height:100%;background:${fill};border-radius:${radius};${sw ? `border:${sw}px solid ${stroke};` : ""}"></div>`;
    } else if (overlay.shape === "star") {
      innerHtml = `<svg viewBox="0 0 24 24" width="100%" height="100%" preserveAspectRatio="xMidYMid meet"><polygon points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/></svg>`;
    } else if (overlay.shape === "arrow") {
      innerHtml = `<svg viewBox="0 0 24 24" width="100%" height="100%" preserveAspectRatio="xMidYMid meet"><polygon points="2,10 14,10 14,6 22,12 14,18 14,14 2,14" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/></svg>`;
    } else {
      // triangle
      innerHtml = `<svg viewBox="0 0 24 24" width="100%" height="100%" preserveAspectRatio="xMidYMid meet"><polygon points="12,3 22,21 2,21" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/></svg>`;
    }
  } else if (overlay.kind === "badge") {
    const fill = overlay.fill ? resolveColor(overlay.fill) : colors.accent;
    const textColor = overlay.textColor ? resolveColor(overlay.textColor) : "#fff";
    innerHtml = `<div style="display:inline-flex;align-items:center;gap:8px;padding:${Math.round(overlay.fontSize * 0.3)}px ${Math.round(overlay.fontSize * 0.8)}px;border-radius:9999px;background:${fill};color:${textColor};font-family:Inter,sans-serif;font-weight:800;font-size:${overlay.fontSize}px;letter-spacing:1px;text-transform:uppercase;">${overlay.icon ? `<span>${escapeHtml(overlay.icon)}</span>` : ""}${escapeHtml(overlay.text || "")}</div>`;
  } else if (overlay.kind === "image") {
    const radius = overlay.radius ?? 0;
    const fit = overlay.fit ?? "cover";
    dimStyle = `width:${overlay.widthPct}%;height:${overlay.heightPct}%;`;
    const src = normaliseAssetSrc(overlay.src);
    innerHtml = `<img src="${escapeHtml(src)}" style="width:100%;height:100%;object-fit:${fit};border-radius:${radius}px;" />`;
  } else if (overlay.kind === "icon") {
    // Icons rendered as text glyphs — Lucide isn't available in pure HTML.
    // For full parity, port lucide-svg into a static map (TODO phase 3c).
    dimStyle = `width:${overlay.sizePct}%;`;
    const color = overlay.color ? resolveColor(overlay.color) : colors.fg;
    innerHtml = `<div style="font-family:'Outfit',sans-serif;color:${color};font-size:${overlay.sizePct * 6}px;font-weight:700;">${escapeHtml(overlay.icon)}</div>`;
  }

  return `<div id="${wrapperId}" class="clip" data-start="${overlay.fromSec}" data-duration="${overlay.durSec}" data-track-index="${overlayTrack}" data-overlay-uid="${escapeHtml(overlay.uid)}" data-overlay-kind="${overlay.kind}" style="${wrapperStyle}${dimStyle}">${innerHtml}</div>`;
}
